import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { ConnectButton } from "./microsoft365/ConnectButton";
import { SecureConnectionStatus } from "./microsoft365/SecureConnectionStatus";
import { ServiceToggle } from "./microsoft365/ServiceToggle";
import { useMicrosoftConnection } from "@/hooks/useMicrosoftConnection";
import { Shield, AlertTriangle, RefreshCw } from "lucide-react";
import { useEffect } from "react";
import { useConnectionHealth } from "@/hooks/useConnectionHealth";
import { ConnectionHealthMonitor } from "@/services/connectionHealthMonitor";

export function Microsoft365Settings() {
  const {
    connectionStatus,
    serviceSettings,
    initiateConnection,
    disconnect,
    updateServiceSetting,
    checkConnectionStatus
  } = useMicrosoftConnection();

  const {
    healthStatus,
    isChecking: isCheckingHealth,
    lastError: healthError,
    checkHealth,
    refreshTokens,
  } = useConnectionHealth(connectionStatus.isConnected);

  const { isConnected, isConnecting, needsRefresh, error } = connectionStatus;

  // Check connection status on mount
  useEffect(() => {
    checkConnectionStatus();
  }, [checkConnectionStatus]);

  const handleRefreshConnection = async () => {
    const success = await refreshTokens();
    if (success) {
      await checkConnectionStatus();
    }
  };

  const getHealthSummary = () => {
    if (!healthStatus) return null;
    return ConnectionHealthMonitor.getHealthSummary(healthStatus);
  };

  const healthSummary = getHealthSummary();

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Shield className="h-5 w-5 text-blue-600" />
            <CardTitle>Microsoft 365 Integration</CardTitle>
          </div>
          <CardDescription>
            Connect your Microsoft 365 account to sync emails and calendar events with encrypted credentials
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <SecureConnectionStatus 
            onStatusChange={checkConnectionStatus}
          />
          
          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {healthError && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{healthError}</AlertDescription>
            </Alert>
          )}

          {/* Connection Health Status */}
          {isConnected && healthStatus && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Connection Health</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={checkHealth}
                  disabled={isCheckingHealth}
                >
                  <RefreshCw className={`h-4 w-4 ${isCheckingHealth ? 'animate-spin' : ''}`} />
                </Button>
              </div>
              
              {healthSummary && (
                <div className={`flex items-center space-x-2 text-sm ${healthSummary.color}`}>
                  <span>{healthSummary.icon}</span>
                  <span>{healthSummary.message}</span>
                </div>
              )}

              {healthStatus.issues.length > 0 && (
                <div className="space-y-2">
                  {healthStatus.issues.map((issue, index) => (
                    <Alert key={index} variant={issue.severity === 'error' ? 'destructive' : 'default'}>
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>
                        <strong>{issue.service}:</strong> {issue.message}
                        {issue.suggestedAction && (
                          <div className="mt-1 text-xs opacity-80">
                            {issue.suggestedAction}
                          </div>
                        )}
                      </AlertDescription>
                    </Alert>
                  ))}
                </div>
              )}
            </div>
          )}

          {!isConnected && (
            <ConnectButton 
              isConnecting={isConnecting}
              isConnected={isConnected}
              onClick={initiateConnection}
            />
          )}

          {isConnected && (
            <div className="space-y-4">
              {needsRefresh && (
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription className="flex items-center justify-between">
                    <span>Your connection will expire soon. Please refresh to ensure continued access.</span>
                    <Button 
                      size="sm" 
                      onClick={handleRefreshConnection}
                      disabled={isConnecting}
                    >
                      Refresh Now
                    </Button>
                  </AlertDescription>
                </Alert>
              )}

              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Connected Services</span>
                <Badge variant="secondary">Secure</Badge>
              </div>

              <div className="space-y-3">
                <ServiceToggle
                  label="Calendar Access"
                  description="Sync calendar events and create meetings"
                  checked={serviceSettings.calendar_enabled}
                  onCheckedChange={(checked) => updateServiceSetting('calendar', checked)}
                  disabled={isConnecting}
                />
                <ServiceToggle
                  label="Email Access" 
                  description="Access and manage Outlook emails"
                  checked={serviceSettings.outlook_enabled}
                  onCheckedChange={(checked) => updateServiceSetting('outlook', checked)}
                  disabled={isConnecting}
                />
              </div>

              <Separator />

              <div className="flex justify-between items-center">
                <div>
                  <h4 className="font-medium">Refresh Connection</h4>
                  <p className="text-sm text-muted-foreground">
                    Refresh your Microsoft 365 tokens and test connectivity
                  </p>
                </div>
                <Button 
                  variant="outline"
                  onClick={handleRefreshConnection}
                  disabled={isConnecting}
                >
                  {isConnecting ? 'Refreshing...' : 'Refresh'}
                </Button>
              </div>

              <div className="flex justify-between items-center">
                <div>
                  <h4 className="font-medium">Reconnect Account</h4>
                  <p className="text-sm text-muted-foreground">
                    Re-authorize your Microsoft 365 connection
                  </p>
                </div>
                <ConnectButton 
                  isConnecting={isConnecting}
                  isConnected={isConnected}
                  onClick={initiateConnection}
                />
              </div>

              <div className="flex justify-between items-center">
                <div>
                  <h4 className="font-medium">Disconnect Account</h4>
                  <p className="text-sm text-muted-foreground">
                    Remove Microsoft 365 integration and delete stored credentials
                  </p>
                </div>
                <Button 
                  variant="destructive" 
                  onClick={disconnect}
                  disabled={isConnecting}
                >
                  Disconnect
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
